/**
 * Os seis atributos do personagem.
 *
 * <p>Definidos pelo Theo em 07/08/2026. Antes disso não existiam em lugar nenhum — a
 * spec da Criação do Mundo diz que balanceamento é assunto do jogo, e por isso o GG
 * Pilot nunca guardou atributo de ficha.
 *
 * <p>Eles moram aqui, e não numa tabela, porque a lista é <b>fechada</b>: seis, com
 * sigla fixa. Quem escreve o jogo não acrescenta um sétimo pela tela; se um dia
 * acrescentar, é mudança de sistema de regras, não de cadastro.
 *
 * <p>A <b>sigla</b> é o que aparece na equação das tier lists, onde o espaço é curto e
 * a fórmula precisa se ler numa linha. O nome inteiro fica na Ficha.
 */
export interface Atributo {
  sigla: string;
  nome: string;
  /** Uma frase do que ele governa — o suficiente para a Ficha não ser só uma lista. */
  sobre: string;
  /**
   * A cor do atributo.
   *
   * <p>Ele precisava de uma pelo mesmo motivo que a perícia tem: sem cor nem ícone, seis
   * cartões idênticos viram uma planilha, e o olho não acha o que procura sem ler.
   *
   * <p>As seis <b>andam pelo espectro na ordem da lista</b>, de terracota a ametista.
   * Não é enfeite: a grade inteira lê como uma escala, e a posição de cada atributo vira
   * memorizável — quem já viu a tela sabe que o azul-aço é a Percepção antes de ler.
   *
   * <p>São mais minerais que as das perícias, e isso separa as duas seções: a de baixo
   * é vívida porque cada perícia é uma coisa do mundo; esta é mais sóbria porque os seis
   * são o mesmo tipo de coisa.
   */
  cor: string;
}

/*
 * A ordem é a da ficha do alpha, lida linha a linha: FOR|AGI, VIT|INT, DES|PER. Ela vale
 * a pena copiar porque a grade daqui tem duas colunas como a de lá — quem olha as duas
 * telas encontra cada atributo no mesmo lugar.
 */
export const ATRIBUTOS: Atributo[] = [
  {
    sigla: 'FOR',
    nome: 'Força',
    sobre: 'O que se carrega, o que se quebra, o quanto o golpe pesa.',
    cor: '#c06a5a',
  },
  {
    sigla: 'AGI',
    nome: 'Agilidade',
    sobre: 'Velocidade de reação, esquiva e deslocamento.',
    cor: '#c2954f',
  },
  {
    sigla: 'VIT',
    nome: 'Vitalidade',
    sobre: 'Quanto o corpo aguenta antes de ceder.',
    cor: '#6fae72',
  },
  /*
   * Era **SAB Sabedoria**, e virou Intelecto porque é assim que o jogo a chama. Sigla e
   * nome mudaram juntos, e a sigla é o que fica gravado nas rolagens — por isso
   * `rolagens.ts` troca `SAB` por `INT` ao ler o rascunho, senão a fórmula montada
   * apontaria para um atributo que não existe mais e o campo apareceria vazio.
   */
  {
    sigla: 'INT',
    nome: 'Intelecto',
    sobre: 'O que se sabe e o que se deduz — leitura de mundo.',
    cor: '#5fa9b5',
  },
  {
    sigla: 'DES',
    nome: 'Destreza',
    sobre: 'A mão firme: precisão, pontaria e trabalho fino.',
    cor: '#7396c4',
  },
  {
    sigla: 'PER',
    nome: 'Percepção',
    sobre: 'Notar o que está ali: a trilha, o veio, a emboscada.',
    cor: '#9878bd',
  },
];

/** A sigla que o alpha aposentou, e para onde ela foi. Lida por `rolagens.ts`. */
export const SIGLA_APOSENTADA: Record<string, string> = { SAB: 'INT' };

export const atributoPorSigla = (sigla: string) => ATRIBUTOS.find((a) => a.sigla === sigla);

/**
 * Quantas linhas de bônus cada atributo tem na Ficha.
 *
 * <p>Seis, uma por atributo — pedido do Theo. O número fixo é o que faz a Ficha ser uma
 * grade comparável: dá para ler na diagonal o que cada atributo dá no mesmo degrau, e
 * um campo em branco é uma pergunta em aberto, visível.
 */
export const LINHAS_DE_BONUS = 6;
